import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCcw, RotateCcw } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import NewsDetailModal from './NewsDetailModal';
import { getLaunchNews, markNewsRead, markNewsSkipped, markNewsViewed } from '../services/newsApi';
import { APP_VERSION } from '../generated/appVersion';
import { isUpdateNews, isUpdateRequiredNow, reloadForAppUpdate } from '../utils/appUpdates';
import { haptic } from '../utils/vibration';

const STORAGE_KEY = 'skipped_news_ids';
const PREVIEW_UPDATE_ID = 'pwa-update-preview';

const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

const buildPreviewUpdateNews = () => ({
  id: PREVIEW_UPDATE_ID,
  category: 'update',
  title: 'Доступно обновление приложения',
  content: 'Локальный просмотр анимации обновления PWA. В реальном обновлении после подготовки новой версии экран перезапустится автоматически.',
  app_version: APP_VERSION,
  is_required_update: false,
  update_is_required_now: false,
  created_at: new Date().toISOString()
});

const getSkippedIds = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
};

const setSkippedIds = (ids) => {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
};

const NewsStartupModal = ({ enabled = true, previewMode = false, canSkipRequiredUpdates = false }) => {
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const routeKey = `${location.pathname || '/'}${location.search || ''}`;

  useEffect(() => {
    if (!enabled) return;

    if (previewMode) {
      setItems([buildPreviewUpdateNews()]);
      return;
    }

    let cancelled = false;

    const load = async () => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      try {
        const data = await getLaunchNews(APP_VERSION);
        if (!cancelled) {
          setItems(Array.isArray(data?.items) ? data.items : []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Ошибка загрузки стартовых новостей:', error);
        }
      }
    };

    load();
    const intervalId = window.setInterval(load, 10 * 60 * 1000);
    const handleFocus = () => load();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        load();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, previewMode, routeKey]);

  const currentNews = useMemo(() => {
    if (previewMode) return items[0] || null;
    const skipped = new Set(getSkippedIds());
    return items.find((item) => (
      (isUpdateRequiredNow(item) && !canSkipRequiredUpdates) || !skipped.has(item.id)
    )) || null;
  }, [canSkipRequiredUpdates, items, previewMode]);

  useEffect(() => {
    if (!currentNews || previewMode) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    markNewsViewed(currentNews.id).catch((error) => {
      console.error('Ошибка учёта просмотра новости:', error);
    });
  }, [currentNews?.id, previewMode]);

  const handleSkip = () => {
    if (!currentNews) return;
    if (actionLoading) return;
    if (isUpdateRequiredNow(currentNews) && !canSkipRequiredUpdates) return;
    haptic('selection');
    setSkippedIds(Array.from(new Set([...getSkippedIds(), currentNews.id])));
    if (!previewMode && (typeof navigator === 'undefined' || navigator.onLine !== false)) {
      markNewsSkipped(currentNews.id).catch((error) => {
        console.error('Ошибка сохранения пропуска новости:', error);
      });
    }
    setItems((prev) => [...prev]);
  };

  const handleConfirm = async () => {
    if (!currentNews) return;
    if (actionLoading) return;
    if (isUpdateNews(currentNews)) {
      setActionLoading(true);
      haptic('selection');
      try {
        if (previewMode) {
          await wait(3500);
          return;
        }

        if (!isUpdateRequiredNow(currentNews)) {
          try {
            await markNewsRead(currentNews.id);
          } catch (error) {
            console.error('Ошибка отметки обновления:', error);
          }
        }
        await reloadForAppUpdate();
      } catch (error) {
        console.error('Ошибка запуска обновления:', error);
      } finally {
        setActionLoading(false);
      }
      return;
    }
    setActionLoading(true);
    try {
      await markNewsRead(currentNews.id);
      haptic('success');
      setItems((prev) => prev.filter((item) => item.id !== currentNews.id));
      setSkippedIds(getSkippedIds().filter((id) => id !== currentNews.id));
    } catch (error) {
      console.error('Ошибка отметки новости:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const updateRequired = isUpdateRequiredNow(currentNews);
  const isUpdate = isUpdateNews(currentNews);
  const canSkip = (!updateRequired || canSkipRequiredUpdates) && !actionLoading;

  return (
    <NewsDetailModal
      news={currentNews}
      isOpen={!!currentNews}
      onClose={handleSkip}
      onConfirm={handleConfirm}
      loading={actionLoading}
      confirmLabel={isUpdate ? (actionLoading ? 'Обновляем...' : 'Обновить') : 'Понятно'}
      confirmIcon={isUpdate ? RefreshCcw : undefined}
      canClose={canSkip}
      secondaryAction={canSkip ? (
        <button
          type="button"
          onClick={handleSkip}
          disabled={actionLoading}
          className="inline-flex items-center gap-2 rounded-2xl border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
        >
          <RotateCcw className="h-4 w-4" />
          Пропустить
        </button>
      ) : null}
    />
  );
};

export default NewsStartupModal;
